<template>
  <div class="form-creation">
    <v-container>
      <v-row align="center">
        <v-col cols="auto">
          <h2 class="">Forge Formsmith</h2>
        </v-col>
        <v-col cols="auto">
          <v-chip
            class="beta-badge"
            color="primary darken-1"
            text-color="black"
            label
          >
            Beta
          </v-chip>
        </v-col>
        <v-col cols="auto">
          <a
            href="https://docs.getforge.com/guides/formsmith-ai--beta"
            target="_blank"
            class="SiteForms__link"
          >
            ?
          </a>
        </v-col>
      </v-row>
    </v-container>
    <p class="bot-message">
      What kind of form would you like to create today? Describe your form and
      we'll do the rest.
    </p>
    <div class="form-group">
      <v-text-field
        v-model="formDescription"
        label=""
        placeholder="A simple contact form for my business website..."
        filled
        required
        prepend-inner-icon="mdi-sparkles"
        :append-outer-icon="loadingFormAI ? 'mdi-loading mdi-spin' : 'mdi-send'"
        @click:append-outer="generateFormMarkup"
        @keyup.enter="generateFormMarkup"
      ></v-text-field>
    </div>

    <div class="sample-prompts">
      <div class="quickstart-header-row">
        <div>
          <h3 class="quickstart-title">Quick Start Templates</h3>
          <p class="quickstart-subtitle">Choose a template to get started quickly, or describe your own form above.</p>
        </div>
        <v-select
          v-model="selectedCategory"
          :items="categoryOptions"
          label="Filter by category"
          dense
          outlined
          class="category-filter-dropdown"
        />
      </div>
      <div class="carousel-container">
        <v-btn
          icon
          class="carousel-nav-btn carousel-nav-left"
          @click="scrollLeft"
          :disabled="scrollPosition <= 0"
          :title="`Scroll left (position: ${scrollPosition}, max: ${maxScroll})`"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        
        <div class="carousel-wrapper" ref="carouselWrapper">
          <div 
            class="carousel-content" 
            ref="carouselContent"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="stopDrag"
            @mouseleave="stopDrag"
          >
            <div
              v-for="prompt in filteredPrompts"
              :key="prompt.text"
              class="carousel-item"
            >
              <v-card
                max-width="260"
                min-height="160"
                @click="selectPrompt(prompt.text)"
                :class="['prompt-card', 'dark-text', { selected: prompt.text === selectedPromptText }]"
                rounded
                :style="{ background: prompt.gradient }"
              >
                <div class="category-chip-absolute">
                  <v-chip
                    x-small
                    color="rgba(255,255,255,0.2)"
                    text-color="#333"
                  >
                    {{ prompt.category }}
                  </v-chip>
                </div>
                <v-card-title>{{ prompt.title }}</v-card-title>
                <v-card-text>{{ prompt.description }}</v-card-text>
              </v-card>
            </div>
          </div>
        </div>
        
        <v-btn
          icon
          class="carousel-nav-btn carousel-nav-right"
          @click="scrollRight"
          :disabled="scrollPosition >= maxScroll"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="result" class="result-block">
      <p>{{ result.text }}</p>
      <v-tabs v-model="activeTab">
        <v-tab>HTML</v-tab>
        <v-tab>CSS</v-tab>
        <v-tab>Preview</v-tab>

        <v-tab-item>
          <div class="codemirror-container">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  @click="copyToClipboard"
                  :loading="copyLoading"
                  class="copy-button"
                >
                  <v-icon>mdi-clipboard</v-icon>
                </v-btn>
              </template>
              <span>Copy HTML to Clipboard</span>
            </v-tooltip>
            <codemirror v-model="result.markup" :options="htmlOptions" />
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="codemirror-container">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  @click="copyToClipboard"
                  :loading="copyLoading"
                  class="copy-button"
                >
                  <v-icon>mdi-clipboard</v-icon>
                </v-btn>
              </template>
              <span>Copy CSS to Clipboard</span>
            </v-tooltip>
            <codemirror v-model="result.style" :options="cssOptions" />
          </div>
        </v-tab-item>

        <v-tab-item>
          <SiteFormPreview
            :markup="result.markup"
            :style="result.style || ''"
          />
        </v-tab-item>
      </v-tabs>
      <v-row>
        <v-col>
          <v-alert type="info" dense>
            Disclaimer: AI can make mistakes. Please check important information
            <v-btn
              text
              href="https://doc.getforge.com/docs/forge-ai"
              target="_blank"
              >here</v-btn
            >.
          </v-alert>
        </v-col>
      </v-row>
    </div>

    <v-snackbar v-model="showSnackbar" :timeout="3000" bottom right>
      Code copied to clipboard
    </v-snackbar>
  </div>
</template>

<script>
import { codemirror } from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/htmlmixed/htmlmixed.js'
import 'codemirror/mode/css/css.js'
import 'codemirror/theme/base16-dark.css'
import SiteFormPreview from './SiteFormPreview.vue'
import promptApiService from '@/services/promptApiService'
import { PROMPT_IDS } from '@/constants/prompts'

export default {
  name: 'SiteFormCreation',
  components: {
    codemirror,
    SiteFormPreview,
  },
  data() {
    return {
      formDescription: '',
      result: null,
      loadingFormAI: false,
      copyLoading: false,
      showSnackbar: false,
      activeTab: 0,
      scrollPosition: 0,
      maxScroll: 0,
      isDragging: false,
      startX: 0,
      dragScrollLeft: 0,
      selectedCategory: 'All',
      selectedPromptText: '',
      samplePrompts: [
        {
          title: 'Simple Signup Form',
          description:
            'Create a simple signup form with name, email, and password fields.',
          text: 'Create a simple signup form with name, email, and password fields.',
          gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
          category: 'Authentication',
        },
        {
          title: 'Contact Form',
          description:
            'Create a contact form with name, email, subject, and message fields.',
          text: 'Create a contact form with name, email, subject, and message fields.',
          gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          category: 'Communication',
        },
        {
          title: 'Newsletter Subscription',
          description:
            'Create a newsletter subscription form with email and preferences selection.',
          text: 'Create a newsletter subscription form with email and preferences selection.',
          gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
          category: 'Communication',
        },
        {
          title: 'Event Registration Form',
          description:
            'Create an event registration form with fields for name, email, and event details.',
          text: 'Create an event registration form with fields for name, email, and event details.',
          gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          category: 'Events',
        },
        {
          title: 'Appointment Booking',
          description:
            'Create an appointment booking form with date, time, and service selection.',
          text: 'Create an appointment booking form with date, time, and service selection.',
          gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
          category: 'Events',
        },
        {
          title: 'NPS Survey',
          description:
            'Create a Net Promoter Score survey with a rating scale from 1 to 10.',
          text: 'Create a Net Promoter Score survey with a rating scale from 1 to 10.',
          gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          category: 'Feedback',
        },
        {
          title: 'Product Feedback Form',
          description:
            'Create a product feedback form with rating, comments, and improvement suggestions.',
          text: 'Create a product feedback form with rating, comments, and improvement suggestions.',
          gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          category: 'Feedback',
        },
        {
          title: 'Customer Satisfaction Survey',
          description:
            'Create a customer satisfaction survey with multiple rating questions and comments.',
          text: 'Create a customer satisfaction survey with multiple rating questions and comments.',
          gradient: 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
          category: 'Feedback',
        },
        {
          title: 'Customer Support Ticket',
          description:
            'Create a support ticket form with issue type, priority, and detailed description.',
          text: 'Create a support ticket form with issue type, priority, and detailed description.',
          gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
          category: 'Support',
        },
        {
          title: 'Job Application Form',
          description:
            'Create a job application form with personal details, experience, and resume upload.',
          text: 'Create a job application form with personal details, experience, and resume upload.',
          gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          category: 'Business',
        },
      ],
      htmlOptions: {
        tabSize: 2,
        mode: 'text/html',
        theme: 'base16-dark',
        lineNumbers: true,
      },
      cssOptions: {
        tabSize: 2,
        mode: 'css',
        theme: 'base16-dark',
        lineNumbers: true,
      },
    }
  },
  computed: {
    categoryOptions() {
      const cats = this.samplePrompts.map(p => p.category)
      return ['All', ...Array.from(new Set(cats))]
    },
    filteredPrompts() {
      if (this.selectedCategory === 'All') return this.samplePrompts
      return this.samplePrompts.filter(p => p.category === this.selectedCategory)
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateMaxScroll()
    })
    window.addEventListener('resize', this.calculateMaxScroll)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateMaxScroll)
  },
  methods: {
    async generateFormMarkup() {
      this.loadingFormAI = true

      try {
        // Use prompt ID 2 for form generation
                     const response = await promptApiService.callPrompt(
               PROMPT_IDS.FORM_GENERATION,
               {
                 formDescription: this.formDescription
               },
               {
                 responseJson: true
               }
             )

        const messageContent = response.choices[0]?.message?.content || 'No response received'
        this.result = this.parseJsonResponse(messageContent)
        this.loadingFormAI = false
        this.$emit('formCreated')
      } catch (error) {
        console.error('Error generating form markup:', error)
        
        // Handle specific error types
        const errorInfo = promptApiService.handleError(error)
        console.error('Prompt API Error:', errorInfo)
        
        this.loadingFormAI = false
        
        // Show user-friendly error message
        this.result = {
          text: "Sorry, I couldn't generate the form markup. Please try again.",
          markup: "",
          style: ""
        }
      }
    },
    parseJsonResponse(response) {
      // Remove the code block delimiters if they exist
      const cleanedResponse = response.replace(/```json\n|```/g, '')

      // Replace Unicode characters with their actual symbols
      let cleanedContent = cleanedResponse
        .replace(/\\u003c/g, '<')
        .replace(/\\u003e/g, '>')
        .replace(/\\n/g, '\\n') // Keep newlines escaped
        .replace(/\\"/g, '\\"') // Ensure double quotes remain escaped
        .replace(/\\'/g, "'")

      // Replace any escaped double quotes that may cause issues
      cleanedContent = cleanedContent.replace(/\\\\"/g, '\\"')

      // Ensure proper escaping of special characters
      cleanedContent = cleanedContent.replace(/\\\\/g, '\\')

      // Parse the cleaned JSON string
      return JSON.parse(cleanedContent)
    },

    copyToClipboard() {
      this.copyLoading = true
      let contentToCopy = ''
      if (this.activeTab === 0) {
        contentToCopy = this.result.markup
      } else if (this.activeTab === 1) {
        contentToCopy = this.result.style
      }

      navigator.clipboard
        .writeText(contentToCopy)
        .then(() => {
          this.copyLoading = false
          this.showSnackbar = true
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err)
          this.copyLoading = false
        })
    },
    selectPrompt(prompt) {
      this.formDescription = prompt
      this.selectedPromptText = prompt
      this.generateFormMarkup()
    },
    scrollLeft() {
      // Recalculate maxScroll to ensure it's accurate
      this.calculateMaxScroll()
      if (this.scrollPosition > 0) {
        this.scrollPosition -= 1
        this.updateScroll()
      }
    },
    scrollRight() {
      // Recalculate maxScroll to ensure it's accurate
      this.calculateMaxScroll()
      if (this.scrollPosition < this.maxScroll) {
        this.scrollPosition += 1
        this.updateScroll()
      }
    },
    updateScroll() {
      if (this.$refs.carouselContent) {
        const itemWidth = 280 // card width + margin
        this.$refs.carouselContent.style.transform = `translateX(-${this.scrollPosition * itemWidth}px)`
        // Disable transition during drag for smoother experience
        if (this.isDragging) {
          this.$refs.carouselContent.style.transition = 'none'
        } else {
          this.$refs.carouselContent.style.transition = 'transform 0.3s ease'
        }
      }
    },
    calculateMaxScroll() {
      if (this.$refs.carouselWrapper && this.$refs.carouselContent) {
        const containerWidth = this.$refs.carouselWrapper.offsetWidth
        const contentWidth = this.$refs.carouselContent.scrollWidth
        const itemWidth = 280
        this.maxScroll = Math.max(0, Math.floor((contentWidth - containerWidth) / itemWidth))
      }
    },
    startDrag(e) {
      this.isDragging = true
      this.startX = e.pageX - this.$refs.carouselContent.offsetLeft
      this.dragScrollLeft = this.scrollPosition * 280
      this.$refs.carouselContent.style.cursor = 'grabbing'
    },
    onDrag(e) {
      if (!this.isDragging) return
      e.preventDefault()
      
      const x = e.pageX - this.$refs.carouselContent.offsetLeft
      const walk = (this.startX - x) * 2
      const newScrollLeft = this.dragScrollLeft + walk
      
      // Calculate new scroll position
      const itemWidth = 280
      const newScrollPosition = Math.max(0, Math.min(this.maxScroll, Math.round(newScrollLeft / itemWidth)))
      
      if (newScrollPosition !== this.scrollPosition) {
        this.scrollPosition = newScrollPosition
        this.updateScroll()
      }
    },
    stopDrag() {
      this.isDragging = false
      if (this.$refs.carouselContent) {
        this.$refs.carouselContent.style.cursor = 'grab'
      }
    },
    resetCarousel() {
      this.scrollPosition = 0
      this.calculateMaxScroll()
      this.updateScroll()
    },
  },
}
</script>

<style scoped>
.form-creation {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 100%;
  overflow-x: hidden;
}

.form-group {
  margin-bottom: 16px;
  max-width: 100%;
}

.result-block {
  margin-top: 20px;
  background-color: #292929;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-width: 100%;
  overflow-x: auto;
}

.codemirror-container {
  position: relative;
  max-width: 100%;
  overflow-x: auto;
}

.copy-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  background-color: #ffffff;
}

.copy-button span i {
  color: #151515 !important;
}

.copy-button .mdi-loading {
  color: #151515 !important;
}

.CodeMirror {
  height: auto;
  max-height: 400px;
  background-color: #151515;
  color: #fff;
  max-width: 100%;
}

.mdi-loading {
  animation: spin 1s linear infinite;
}

.bot-message {
  background-color: #e0e0e0;
  color: #333;
  padding: 10px 15px;
  border-radius: 15px;
  position: relative;
  margin-top: 20px;
  margin-bottom: 20px;
  display: inline-block;
  width: 40%;
  max-width: 100%;
}

.bot-message::before {
  content: '';
  position: absolute;
  left: 10px;
  bottom: -6px;
  width: 0;
  height: 0;
  border-top: 10px solid #e0e0e0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
}

.sample-prompts {
  margin-bottom: 20px;
  max-width: 100%;
  overflow-x: hidden;
}

.quickstart-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #fff;
}

.quickstart-subtitle {
  font-size: 0.9rem;
  color: #fff;
  margin-bottom: 20px;
}

.quickstart-header-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  max-width: 100%;
}

.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  max-width: 100%;
  overflow-x: hidden;
}

.carousel-wrapper {
  flex: 1;
  overflow: hidden;
  margin: 0 50px;
  padding: 10px 0;
  max-width: calc(100% - 100px);
}

.carousel-content {
  display: flex;
  transition: transform 0.3s ease;
  gap: 20px;
  padding: 10px 0;
  cursor: grab;
  user-select: none;
  max-width: 100%;
}

.carousel-content:active {
  cursor: grabbing;
}

.carousel-item {
  flex-shrink: 0;
  padding: 5px;
  max-width: 260px;
}

.carousel-nav-btn {
  position: absolute;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.9) !important;
  color: #333 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.carousel-nav-btn:hover {
  background-color: rgba(255, 255, 255, 1) !important;
}

.carousel-nav-btn:disabled {
  opacity: 0.1;
  cursor: not-allowed;
}

.carousel-nav-left {
  left: 0;
}

.carousel-nav-right {
  right: 0;
}

.prompt-card {
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
  padding-top: 32px;
  max-width: 260px;
  width: 100%;
}

.prompt-card .v-card__title {
  color: #333 !important;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 0;
  padding-top: 0;
}

.prompt-card .v-card__text {
  color: rgba(51, 51, 51, 0.8) !important;
}

.category-chip {
  font-size: 10px !important;
  font-weight: 500;
  margin-left: auto;
}

.category-chip-absolute {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 2;
}

.prompt-card:hover {
  transform: scale(1.05);
}

.prompt-card .v-card__title {
  font-size: 14px !important;
  font-weight: 900;
}

.prompt-card.selected {
  outline: 3px solid #1976d2;
  box-shadow: 0 0 0 2px #1976d233;
  z-index: 3;
}

.category-filter-dropdown {
  margin-bottom: 0;
  min-width: 180px;
  max-width: 220px;
}

/* Responsive styles */
@media (max-width: 768px) {
  .quickstart-header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .carousel-wrapper {
    margin: 0 30px;
  }
  
  .carousel-nav-btn {
    width: 40px;
    height: 40px;
  }
  
  .bot-message {
    width: 100%;
  }
  
  .prompt-card {
    max-width: 240px;
  }
  
  .carousel-item {
    max-width: 240px;
  }
}

@media (max-width: 480px) {
  .carousel-wrapper {
    margin: 0 20px;
  }
  
  .carousel-nav-btn {
    width: 35px;
    height: 35px;
  }
  
  .prompt-card {
    max-width: 220px;
  }
  
  .carousel-item {
    max-width: 220px;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
